/* AddNewUserRoleComponent.js */

"use strict";
const EventEmitter = require("events").EventEmitter;
const _ = require("lodash");

const Renderer = require("./AddNewUserRoleRenderer");

/**
 * @class AddNewUserRoleComponent
 * @extends EventEmitter
 *
 * @fires AddNewUserRoleComponent#Data
 * @fires AddNewUserRoleComponent#Error
 * @fires AddNewUserRoleComponent#Shutdown
 */

class AddNewUserRoleComponent extends EventEmitter {
    constructor(data) {
        super();
        this._data = data;
        this._actionGroupEntityDef = this._data.entityDef;
        this._activeOrgId = this._data.activeOrgId;
        this._actionGroupEntity = new this._actionGroupEntityDef(this._activeOrgId);
        this._manageRoleComponent = this._data.manageRoleComponent;
        this._userFeedback = this._data.userFeedback;
        this._customFieldsWithInherited = this._data.customFieldsWithInherited;
        this._deviceEntity = this._data.deviceEntity;
        this._uiUtils = this._data.uiUtils;
        this._descendantOrgs = this._data.descendantOrgs;
        this._registerListeners();
        this._renderer = new Renderer(this);
    }

    get renderer() {
        return this.getRenderer();
    }

    get rootElement() {
        return this._renderer.getRootElement();
    }

    get activeOrgId() {
        return this._activeOrgId;
    }

    get userFeedback() {
        return this._userFeedback;
    }

    // prettier-ignore
    set "$teve.ui.context"(context) { //jshint ignore:line
        this._context = context;
    }

    /*******************************************/
    /* START PRIVATE COMPONENT MODEL METHODS */

    /*******************************************/

    _registerListeners() {
        this._manageRoleComponent.on("OrganizationChanged", this._onOrganizationChanged.bind(this));
    }

    _unregisterListeners() {
        this._manageRoleComponent.removeListener(
            "OrganizationChanged",
            this._onOrganizationChanged
        );
    }

    _shutdown() {
        this.emit("Shutdown");
        this._unregisterListeners();

        return Promise.all([this._renderer.shutdown()])
            .then(() => {
                this._renderer = undefined;
                this._shutdownProm = undefined;
                this._data = undefined;
                this._actionGroupEntityDef = undefined;
                this._actionGroupEntity = undefined;
                this._manageRoleComponent = undefined;
                this._activeOrgId = undefined;
                this._userFeedback = undefined;
                this._customFieldsWithInherited = undefined;
                this._deviceEntity = undefined;
                this._uiUtils = undefined;
                this.removeAllListeners();
            })
            .catch((error) => {
                console.log(`${this.constructor.name}: error in shutdown`);
                console.error(error);
            });
    }

    getRoles() {
        return this._manageRoleComponent.getRoles().then((roles) => {
            return roles;
        });
    }

    saveActionGroup(_copyFrom, _roleName) {
        return this._copyFromActionGroup(_copyFrom, _roleName)
            .then(() => {
                return this._actionGroupEntity.create();
            })
            .catch((err) => {
                this.emit("Error", err.errorMsg);
                return Promise.reject(err);
            });
    }

    _copyFromActionGroup(_copyFrom, _roleName = "") {
        if (_.isEmpty(_copyFrom) || _copyFrom === "none") {
            return Promise.resolve();
        }

        this._actionGroupEntity = new this._actionGroupEntityDef(this._activeOrgId);
        this._actionGroupEntity.setName(_roleName);

        return this._manageRoleComponent
            ._retrieveActionGroup(_copyFrom)
            .then((actionGroupToCopyFrom) => {
                this._actionGroupEntity.setShared(false);
                this._actionGroupEntity.setDescription(
                    _.get(actionGroupToCopyFrom, "pristineProperties.description", "")
                );
                this._actionGroupEntity.setEnabled(
                    _.get(actionGroupToCopyFrom, "pristineProperties.enabled", false)
                );
                this._actionGroupEntity.setDeviceFilter(
                    _.get(actionGroupToCopyFrom, "deviceFilter", null)
                );

                this._actionGroupEntity.setOrgVis(
                    _.get(actionGroupToCopyFrom, "pristineProperties.orgVis", "DISABLED")
                );
                this._actionGroupEntity.setVisibleOrgs(
                    _.get(actionGroupToCopyFrom, "pristineProperties.visibleOrgs", {})
                );

                _.forEach(
                    Object.keys(_.get(actionGroupToCopyFrom, "pristineProperties.actions", {})),
                    (action) => {
                        this._actionGroupEntity.addActionAuth(action);
                    }
                );

                _.forEach(
                    Object.keys(_.get(actionGroupToCopyFrom, "pristineProperties.notifEmails", {})),
                    (action) => {
                        this._actionGroupEntity.setActionDefaultEmailAddress(
                            action,
                            _.get(actionGroupToCopyFrom, "pristineProperties.notifEmails", {})[
                                action
                            ]
                        );
                    }
                );
            });
    }

    /*******************************************/
    /* END PRIVATE COMPONENT MODEL METHODS */
    /* START PUBLIC COMPONENT MODEL METHODS */

    /*******************************************/
    getRenderer() {
        return this._renderer;
    }

    shutdown() {
        if (this._shutdownProm === null) {
            return Promise.resolve();
        }
        this._shutdownProm = this._shutdownProm || this._shutdown();
        return this._shutdownProm;
    }

    closePopUp() {
        this.rejectPopUp({userRejected: true});
    }

    resolvePopup(data) {
        this._context.resolve(data);
    }

    rejectPopUp(data) {
        this._context.reject(data);
    }

    /*******************************************/
    /* END COMPONENT MODEL METHODS */
    /* START HANDLER METHODS */
    /*******************************************/

    _onOrganizationChanged(orgObj) {
        if (this.activeOrgId === orgObj.id) {
            return;
        }
        this._activeOrgId = orgObj.id;

        this.closePopUp();
    }

    /*******************************************/
    /* END HANDLER METHODS */
    /* START PRIVATE METHODS */
    /*******************************************/

    /*******************************************/
    /* END PRIVATE METHODS */
    /* START PUBLIC API METHODS */
    /*******************************************/

    /*******************************************/
    /* END PUBLIC API METHODS */
    /*******************************************/
}

module.exports = AddNewUserRoleComponent;
